import { styled, useTheme } from '@mui/material/styles';
import {
  List,
  IconButton,
  ListItem,
  ListItemButton,
  useMediaQuery,
  Box,
  Typography,
  Skeleton,
} from '@mui/material';
import LoginButton from '../LoginButton';

import { Close } from '@mui/icons-material';
import MuiDrawer from '@mui/material/Drawer';
import { Link, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useTranslation } from 'react-i18next';
import { sidebarData } from '../../../../utils/data';
import { useEffect, useState } from 'react';
import SidebarListItem from './SidebarListItem';
import { capitalize } from '../../../../utils/helper';
import { useUserInfoMenu, UserAvatar } from '../UserInfo';
import Dropdown from '../../../common/DropDown';
import UpgradePlanButton from '../UpgradePlanButton';
import useUsersFacade from '../../../../store/profile/useUsersFacade';

const drawerWidth = 234;

const openedMixin = (theme) => ({
  width: drawerWidth,
  left: '16px !important',
  top: '16px !important',
  bottom: '16px !important',
  right: 'auto !important',
  marginRight: '16px',
  height: 'auto !important',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  [theme.breakpoints.down('md')]: {
    left: '0 !important',
    top: '0 !important',
    bottom: '0 !important',
    right: '0 !important',
  },
});

const closedMixin = (theme) => ({
  left: '16px !important',
  top: '16px !important',
  bottom: '16px !important',
  right: 'auto !important',
  marginRight: '16px',
  height: 'auto !important',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(18)})`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(18)})`,
  },
});

// Add custom scrollbar styles
const ScrollableContainer = styled('div')(() => ({
  height: '100%',
  overflowY: 'auto',
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '6px', // Make scrollbar thinner
    backgroundColor: 'transparent', // Transparent background
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#9E9E9E', // Handle color as requested
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent', // Transparent track
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: '#9E9E9E transparent',
}));

const DrawerHeader = styled('div')(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(4, 1, 10, 1),

  // necessary for content to be below app bar
  ...(open && {
    padding: theme.spacing(6, 6, 8, 6),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4, 6, 4, 6),
    },
  }),
  ...(!open && {
    padding: theme.spacing(8, 4, 12.5, 4),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(4, 6, 4, 6),
    },
  }),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',

  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
    '.MuiPaper-root': {
      backgroundColor: theme.palette.mode === 'dark' ? '#222' : '#F9FCFF',
      borderRadius: theme.spacing(4),
      boxShadow: 'none',
      borderRight: 0,
      // Remove default scrollbar styling from Drawer
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      scrollbarWidth: 'none', // For Firefox
      msOverflowStyle: 'none', // For IE and Edge
      overflow: 'hidden',

      [theme.breakpoints.down('md')]: {
        borderRadius: theme.spacing(0),
        width: '100% !important',
      },
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
    '.MuiPaper-root': {
      borderRadius: theme.spacing(4),
      backgroundColor:
        theme.palette.mode === 'dark' ? '#222' : theme.palette.secondary.paper,
      boxShadow: 'none',
      borderRight: 0,
      // Remove default scrollbar styling from Drawer
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      scrollbarWidth: 'none', // For Firefox
      msOverflowStyle: 'none', // For IE and Edge
      overflow: 'hidden',

      '&:hover': {
        cursor: 'e-resize',
      },
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
  }),
}));

const LogoHeader = styled('img', {
  shouldForwardProp: (prop) => prop !== 'mobile',
})(({ theme, mobile }) => ({
  width: '30px',
  ...(mobile
    ? {
        display: 'none',
        [theme.breakpoints.down('md')]: {
          display: 'block',
          width: '176px',
        },
      }
    : {
        display: 'block',
        [theme.breakpoints.down('md')]: {
          display: 'none',
        },
      }),
}));

const ImageHeader = styled(Link)(() => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
}));

export const CustomListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'sidebarOpen' && prop !== 'isActive',
})(({ theme, isActive, sidebarOpen }) => ({
  minHeight: 48,
  display: 'flex',
  justifyContent: 'center',
  padding: sidebarOpen ? '8px 16px 8px 8px' : '8px 8px 8px 8px',
  marginBottom: theme.spacing(2),
  borderRadius: '8px',

  // Responsive spacing between md and xl
  [theme.breakpoints.only('md')]: {
    padding: sidebarOpen ? '6px 12px 6px 6px' : '6px 6px 6px 6px',
    marginBottom: theme.spacing(1), // More compact on md
  },
  [theme.breakpoints.only('lg')]: {
    padding: sidebarOpen ? '7px 14px 7px 7px' : '7px 7px 7px 7px',
    marginBottom: theme.spacing(1.25), // Slightly more compact on lg
  },

  // Default (non-active) state colors
  '* , svg': {
    color: theme.palette.mode === 'dark' ? '#C6C6C6' : '#787878',
  },

  // Hover state
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#464646' : '#E1F2FD',
  },

  ...(isActive === 'true' && {
    // Active state background colors
    backgroundColor: theme.palette.mode === 'dark' ? '#464646' : '#E1F2FD',

    '* , svg': {
      // Active state colors: #8CCFF9 for dark mode, #0486D7 for light mode
      color: theme.palette.mode === 'dark' ? '#8CCFF9' : '#0486D7',
      fontWeight: 700,
      fontSize: '14px !important',
      lineHeight: '20px !important',
      fontFamily: "'Poppins', sans-serif",
    },
  }),
}));

export const CustomListItem = styled(ListItem)(({ theme, isactive }) => ({
  position: 'relative',

  '.MuiListItemText-root': {
    marginTop: 0,
    marginBottom: 0,
  },
  ...(isactive === 'true' && {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.secondary.surface_22
        : theme.palette.secondary.secondary_99,
    fontWeight: 'bold',
    '* , svg': {
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
    // '&:after': {
    //   content: "''",
    //   position: 'absolute',
    //   right: 0,
    //   top: 0,
    //   borderRadius: theme.spacing(4),
    //   width: theme.spacing(2),
    //   height: '100%',
    //   backgroundColor:
    //     theme.palette.mode === 'dark'
    //       ? theme.palette.secondary.secondary_60
    //       : theme.palette.secondary.secondary_20,
    // },
  }),
}));

const ToggleIcon = styled('img')(({ theme }) => ({
  width: theme.spacing(5.5),
}));

const UserProfileContainer = styled(Box)(({ theme, open }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: `${theme.spacing(3)} !important`,
  borderRadius: theme.spacing(2),
  width: '100%',
  cursor: 'pointer',
  // borderTop: '1px solid',
  // borderTopColor: theme.palette.mode === 'dark' ? '#464646' : '#E5E5E5',
  // backgroundColor: theme.palette.mode === 'dark'
  //   ? theme.palette.secondary.surface_22
  //   : theme.palette.mode === 'dark' ? '#222' : theme.palette.secondary.paper,
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.secondary.neutral_30
        : theme.palette.secondary.neutral_95,
  },
  ...(!open && {
    justifyContent: 'center',
    padding: `${theme.spacing(3)} ${theme.spacing(1)} !important`,
  }),
}));

const CustomDivier = styled('hr')(({ theme }) => ({
  border: 0,
  height: '1px',
  width: '100%',
  margin: `${theme.spacing(0, 0, 2, 0)} !important`,
  marginRight: 'auto',
  backgroundColor: theme.palette.mode === 'dark' ? '#464646' : '#E5E5E5',

  [theme.breakpoints.only('md')]: {
    margin: theme.spacing(0, 0, 1, 0),
  },
}));
const AvatarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isGuest',
})(({ theme, isGuest }) => ({
  width: theme.spacing(9),
  height: theme.spacing(9),
  borderRadius: '50%',
  backgroundColor: isGuest ? 'transparent' : '#0486D7',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

const UserInfoSection = styled(Box)(({ theme, open }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
  ...(!open && {
    display: 'none',
  }),
}));

const UserName = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 600,
  fontSize: '12px !important',
  lineHeight: '16px !important',
  color: theme.palette.mode === 'dark' ? '#FFFBFF' : '#1A1B1F',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.only('md')]: {
    fontSize: '10px !important',
  },
  [theme.breakpoints.only('lg')]: {
    fontSize: '11px !important',
  },
}));

const PlanBadge = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.secondary.secondary_10
      : '#E1F2FD',
  color: theme.palette.mode === 'dark' ? '#E1F2FD' : '#0486D7',
  borderRadius: '9999px',
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  fontSize: '10px !important',
  lineHeight: '16px !important',
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 400,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'fit-content',
  [theme.breakpoints.only('md')]: {
    fontSize: '8px !important',
  },
  [theme.breakpoints.only('lg')]: {
    fontSize: '9px !important',
  },
}));

const Sidebar = ({
  handleDrawerClose,
  handleDrawerOpen,
  open,
  userInfoData,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const menuConfig = useUserInfoMenu(userInfoData);
  const [isHovered, setIsHovered] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { userInfo } = useUsersFacade();
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  const isPanel = subdomain.includes('panel');

  useEffect(() => {
    if (mdDown) {
      handleDrawerClose();
    }
  }, [location]);

  const handleSidebarClick = (e) => {
    // Only handle clicks when sidebar is collapsed
    if (!open) {
      // Check if the click is not on an interactive element
      const isListItem = e.target.closest(
        '.MuiListItem-root, .MuiListItemButton-root'
      );
      const isIconButton = e.target.closest('.MuiIconButton-root');
      const isProfileSection = e.target.closest('[data-profile-section]');

      // If click is not on any interactive element, expand the sidebar
      if (!isListItem && !isIconButton && !isProfileSection) {
        handleDrawerOpen();
      }
    }
  };

  return (
    <Drawer
      variant="permanent"
      open={open}
      onMouseEnter={() => !isDropdownOpen && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleSidebarClick}
    >
      <DrawerHeader
        open={open}
        sx={{ justifyContent: open ? 'space-between' : 'center' }}
      >
        {open ? (
          <>
            <ImageHeader title="go to website" to="/">
              <LogoHeader
                src={
                  theme.palette.mode === 'dark'
                    ? `/static/img/logo-mini-white.svg`
                    : `/static/img/logo-mini-black.svg`
                }
              />
              <LogoHeader
                mobile
                src={
                  theme.palette.mode === 'dark'
                    ? `/static/img/logo/magapi-horizontal-dark.svg`
                    : `/static/img/logo/magapi-horizontal-light.svg`
                }
              />
            </ImageHeader>
            <IconButton
              sx={{ color: 'primary.main', cursor: 'w-resize' }}
              onClick={handleDrawerClose}
            >
              {mdDown ? (
                <Close />
              ) : (
                <ToggleIcon
                  src={
                    theme.palette.mode === 'dark'
                      ? `/static/img/icon/sidebar-collapse-dark.svg`
                      : `/static/img/icon/sidebar-collapse-light.svg`
                  }
                />
              )}
            </IconButton>
          </>
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ImageHeader
              title="go to website"
              to="/"
              sx={{
                position: 'absolute',
                opacity: isHovered ? 0 : 1,
                transition: 'opacity 80ms ease-in-out',
                pointerEvents: isHovered ? 'none' : 'auto',
              }}
            >
              <LogoHeader
                src={
                  theme.palette.mode === 'dark'
                    ? `/static/img/logo-mini-white.svg`
                    : `/static/img/logo-mini-black.svg`
                }
                sx={{ marginTop: '12.5px' }}
              />
              <LogoHeader
                mobile
                src={
                  theme.palette.mode === 'dark'
                    ? `/static/img/logo/magapi-horizontal-dark.svg`
                    : `/static/img/logo/magapi-horizontal-light.svg`
                }
                sx={{ marginTop: '12.5px' }}
              />
            </ImageHeader>
            <IconButton
              sx={{
                position: 'absolute',
                opacity: isHovered ? 1 : 0,
                transition: 'opacity 80ms ease-in-out',
                pointerEvents: isHovered ? 'auto' : 'none',
                color: 'primary.main',
                cursor: 'e-resize',
              }}
              onClick={handleDrawerOpen}
            >
              <ToggleIcon
                src={
                  theme.palette.mode === 'dark'
                    ? `/static/img/icon/sidebar-collapse-dark.svg`
                    : `/static/img/icon/sidebar-collapse-light.svg`
                }
              />
            </IconButton>
          </Box>
        )}
      </DrawerHeader>
      {/* {mdDown ? (
        <Box sx={{ display: 'flex' }}>
          {userInfoData ? (
            !userInfoData?.payment?.plan_name.toLowerCase().includes('pro') ? (
              <Box
                sx={{
                  px: !smDown ? 4 : 3,
                  py: 4,
                  paddingRight: !smDown ? 4 : 2,
                  width: '100%',
                }}
              >
                <UpgradePlanButton userInfo={userInfoData} />
              </Box>
            ) : null
          ) : null}
        </Box>
      ) : null}{' '} */}
      {/* Wrap List in ScrollableContainer */}
      <ScrollableContainer>
        <List sx={{ padding: '8px' }}>
          {sidebarData.map((item) => (
            <SidebarListItem
              key={item.text}
              item={item}
              t={t}
              sidebarOpen={open}
              depth={0.5}
            />
          ))}
        </List>
      </ScrollableContainer>
      {!userInfo ? (
        <Box
          sx={{
            px: open ? 4 : 2,
            width: open ? '100%' : '10px',
          }}
        >
          <LoginButton
            sx={{ width: open ? '100%' : '10px' }}
            isCollapsed={!open}
            extrnalLink={false}
          />
        </Box>
      ) : !userInfo?.payment?.plan_name.toLowerCase().includes('pro') ? (
        <Box
          sx={{
            px: open ? 4 : 2,
            width: open ? '100%' : '10px',
          }}
        >
          <UpgradePlanButton
            sx={{ width: open ? '100%' : '10px' }}
            userInfo={userInfo}
            isCollapsed={!open}
            extrnalLink={!isPanel}
            enableSunset
            forceTryForFree
          />
        </Box>
      ) : null}
      <Dropdown
        hasHeader={menuConfig.hasHeader}
        hasFooter={menuConfig.hasFooter}
        list={menuConfig.list}
        rightItem
        openUpward={true}
        onOpenChange={setIsDropdownOpen}
        isLogout={!userInfoData}
        sx={{
          padding: theme.spacing(4),
        }}
        menuSx={{
          width: { md: '260px' },
          maxWidth: { md: '260px' },
        }}
      >
        <CustomDivier />
        <UserProfileContainer open={open} data-profile-section="true">
          <AvatarWrapper isGuest={!userInfoData}>
            <UserAvatar userInfoData={userInfoData} />
          </AvatarWrapper>
          <UserInfoSection open={open}>
            {userInfoData?.display_name || userInfoData?.name ? (
              <UserName>
                {userInfoData?.display_name || userInfoData?.name}
              </UserName>
            ) : (
              <>
                <UserName>Guest</UserName>
              </>
            )}
            {userInfoData?.payment?.plan_name ? (
              <PlanBadge>
                {capitalize(userInfoData.payment.plan_name)}
              </PlanBadge>
            ) : null}
          </UserInfoSection>
        </UserProfileContainer>
      </Dropdown>
    </Drawer>
  );
};

export default Sidebar;
